import { ExternalLink } from "@skv/core";
import { AppRoutes } from "@skv/core/src/routes";
import { Link } from "react-router-dom";

const AU_APPLICATION_URL = "www.volunteer.com.au";
const NZ_APPLICATION_URL = "www.seekvolunteer.co.nz";
const NSW_APPLICATION_URL = "www.makeadifference.volunteering.nsw.gov.au";

interface DefinitionsProps {
  showAgency?: boolean;
  showLaw?: boolean;
  showPartners?: boolean;
  showPersonalInformation?: boolean;
  showPrivacyLaws?: boolean;
  showRelatedBodyCorporate?: boolean;
  showServiceProviders?: boolean;
  showContent?: boolean;
  showIntellectualProperty?: boolean;
  showPost?: boolean;
  showWeUs?: boolean;
}

const Definitions = (props: DefinitionsProps) => {
  const noPropsPassed = Object.keys(props).length === 0;

  if (noPropsPassed) {
    return null;
  }

  return (
    <li>
      <strong>Definitions</strong>
      <ul
        className="list-unstyled"
        css={`
          > li {
            margin-bottom: 0.5rem;
          }
        `}
      >
        {props.showAgency && (
          <li>
            <strong>Agency</strong> means an organisation that provides support
            to other Organisations who facilitate volunteer recruitment by
            helping them to manage, attract and shortlist volunteers on the
            Site.
          </li>
        )}
        {props.showLaw && (
          <li>
            <strong>Law</strong> means any statute, regulation, by-law,
            ordinance or subordinate legislation in force from time to time in
            any relevant jurisdiction and includes any applicable common law and
            equity and industry codes of conduct.
          </li>
        )}
        {props.showPartners && (
          <li>
            <strong>Partners</strong> means third parties who work with us to
            provide us or our users with services, for example, recruitment
            software providers that integrate with us to provide our services to
            Organisations or strategic business partners that we work with to
            provide ancillary services.
          </li>
        )}
        {props.showPersonalInformation && (
          <li>
            <strong>Personal Information</strong> means information that
            identifies an individual or could reasonably identify an individual,
            including facts and opinions, whether they are true or not.
          </li>
        )}
        {props.showPrivacyLaws && (
          <li>
            <strong>Privacy Laws</strong> means the applicable privacy and data
            protection laws as may be in force from time to time in a particular
            jurisdiction which regulates the collection, use, disclosure,
            storage of and granting of access rights to Personal Information.
          </li>
        )}
        {props.showRelatedBodyCorporate && (
          <li>
            <strong>Related Body Corporate</strong> or{" "}
            <strong>Related Bodies Corporate</strong> has the meaning given to
            that term in section 50 of the <i>Corporations Act 2001</i> (Cth).
          </li>
        )}
        {props.showServiceProviders && (
          <li>
            <strong>Service Providers</strong> means third parties that help us
            to provide our product and services to you. For example, analytics
            service providers; cloud system service providers; storage service
            providers; information technology service providers; marketing and
            advertising service providers; market research providers;
            accounting, audit, legal and compliance service providers, and other
            professional advisers; service providers who provide services in
            event management, and all other vendors.
          </li>
        )}
        {props.showContent && (
          <li>
            <strong>Content</strong> includes things that You may see, read,
            hear, download or access on or via the Site (including but not
            limited to messages, files, data, software, images, photographs,
            illustrations, text and other materials).
          </li>
        )}
        {props.showIntellectualProperty && (
          <li>
            <strong>Intellectual Property</strong> means all intellectual
            property rights through the world including all registered and
            unregistered rights in respect of copyright, designs, circuit
            layouts, trade marks, trade secrets, know-how, moral rights,
            confidential information.
          </li>
        )}
        {props.showPost && (
          <li>
            <strong>Post</strong> or <strong>Posted</strong> includes uploaded,
            posted, emailed or otherwise electronically transmitted.
          </li>
        )}
        {props.showWeUs && (
          <li>
            <strong>We</strong> or <strong>Us</strong> means SEEK Volunteer Pty
            Ltd (ABN 72 623 503 248).
          </li>
        )}
      </ul>
    </li>
  );
};

const PrivacyContent = (props: { isNZ?: boolean }) => {
  return (
    <>
      <h2>Privacy Statement</h2>
      <ol
        css={`
          > li {
            margin-bottom: 0.5rem;
          }
        `}
      >
        <li>
          <strong>SEEK Volunteer Privacy Statement</strong>
          <ol type="a">
            <li>
              The privacy of your personal information is very important to Seek
              Volunteer Pty Ltd (ABN 72 623 503 248) (<b>SEEK Volunteer</b>).
            </li>
            <li>
              This Privacy Statement applies to any personal information you
              disclose to SEEK Volunteer through your use of our Sites including{" "}
              <a href={`https://${AU_APPLICATION_URL}`}>{AU_APPLICATION_URL}</a>
              ,{" "}
              <a href={`https://${NZ_APPLICATION_URL}`}>{NZ_APPLICATION_URL}</a>
              , and{" "}
              <a href={`https://${NSW_APPLICATION_URL}`}>
                {NSW_APPLICATION_URL}
              </a>{" "}
              (the &ldquo;<b>Sites</b>&rdquo;).
            </li>
            <li>
              By using the Sites, you consent to our use of your personal
              information in accordance with this Privacy Statement.
            </li>
            <li>
              Where used in this Privacy Statement, the terms &ldquo;personal
              information&rdquo;, and &ldquo;Australian Privacy
              Principle(s)&rdquo; have the meaning attributed to those terms in
              the Privacy Act 1988 (Cth) (&ldquo;Privacy Act&rdquo;). Any
              reference to &ldquo;<b>SEEK Volunteer</b>
              &rdquo;, &ldquo;<b>we</b>&rdquo;, &ldquo;<b>us</b>&rdquo; and
              &ldquo;
              <b>our</b>&rdquo; includes a reference to Seek Volunteer Pty Ltd
              and each of Seek Volunteer&apos;s related bodies corporate (as
              that term is defined in the <i>Corporations Act 2001</i> (Cth)).
              Volunteers, Organisations and users who otherwise visit the Site
              are referred to generically in this Statement as &ldquo;
              <b>visitors</b>&rdquo;, &ldquo;<b>users</b>&rdquo; or &ldquo;
              <b>you</b>&rdquo; unless the context provides that only volunteers
              or Organisations are being referred to.
            </li>
          </ol>
        </li>

        <li>
          <strong>Why do I need to collect personal information?</strong>
          <ol type="a">
            <li>
              We need to collect personal information to be able to effectively
              provide the service of connecting volunteers and not-for-profit
              organisations, social enterprises and Agencies who are advertising
              volunteer roles on the Site (<b>Organisations</b>).
            </li>
          </ol>
        </li>

        <li>
          <strong>When is my personal information collected?</strong>
          <ol type="a">
            <li>
              If you are an Organisation, We collect your personal information
              when you:
              <ol>
                <li>register on the Site as an Organisation; or</li>
              </ol>
            </li>
            <li>
              If you are a Volunteer, we collect your personal information when
              you
              <ol>
                <li>
                  supply any information to us by expressing interest in a
                  volunteer opportunity or
                </li>
                <li>
                  save a search result and consent to receive recommended
                  opportunities via email.
                </li>
              </ol>
            </li>
            <li>
              We also collect information (first name, last name, email address)
              when you correspond with us via the &lsquo;Contact Us&rsquo;
              function on our Sites.
            </li>
          </ol>
        </li>

        <li>
          <strong>What personal information do you collect?</strong>
          <ol type="a">
            <li>
              If you express interest in a volunteer position, we collect your
              name and your contact details. We may also collect your year of
              birth and gender, should you wish to share this. You may also
              choose to provide information about yourself such as your skills
              and qualifications, but this is not personal information.
            </li>
            <li>
              If you are an Organisation that registers on our Site, we will
              collect the personal information (name and contact details) of a
              contact at your Organisation so that we can get in touch with you
              regarding your registration, information which may assist you to
              recruit volunteers and research purposes. We will also collect
              some information about your Organisation to provide our potential
              volunteers with relevant information. This information is not
              personal information.
            </li>
          </ol>
        </li>

        <li>
          <strong>What other information do you collect?</strong>
          <ol type="a">
            <li>
              SEEK Volunteer collects non-personal information on Site visits by
              users including, without limitation, which sections of the Site
              are most frequently visited, how often, and for how long. This
              data is always used as aggregated, non-personal information, and
              may be shared with our parent company SEEK Limited to provide them
              with information relating to how visitors &lsquo;use&rsquo; the
              Sites. This is done for the purpose of improving the services we
              offer to you. If you wish, you may elect to not accept cookies on
              your browser.
            </li>
            <li>
              SEEK&apos;s web servers gather your IP address to assist with the
              diagnosis of problems or support issues with our services, and to
              monitor the use of our Site.
            </li>
          </ol>
        </li>

        <li>
          <strong>Will my personal information be given to anyone else?</strong>
          <ol type="a">
            <li>
              If you express interest in a volunteer role, we will pass on the
              information that you provide to us in relation to that particular
              volunteer opportunity to the relevant Organisation. If the
              Organisation is connected to an Agency on SEEK Volunteer, your
              Personal Information may also be shared with the Agency (via
              email, secure technology integration, or directly to the
              Organisation&apos;s volunteer management system) for the purposes
              of them supporting the Organisation with their volunteer
              recruitment activities.
            </li>
            <li>
              SEEK Volunteer may also disclose your personal information to its
              Service Providers, contractors and Partners from time to time but
              only for the purpose of assisting us to deliver the products and
              services we offer to you or to conduct market research about our
              products and services.
            </li>
            <li>
              SEEK Volunteer may also disclose your personal information to its
              related bodies corporate from time to time but only for the
              purpose of assisting SEEK Volunteer and its related bodies
              corporate to provide and improve the products and services offered
              to users of each of their websites and products including (but not
              limited to):
              <ol>
                <li>
                  data enhancement and parsing to facilitate product
                  functionality and service quality;
                </li>
                <li>
                  product development to respond to market changes and customer
                  needs and requests;
                </li>
                <li>
                  development and improvement of algorithms servicing product
                  functionality (e.g. Search recommendations);
                </li>
                <li>
                  IT security measures for the benefit of protecting customer
                  data;
                </li>
                <li>
                  data aggregation to support customer experiences, support
                  services, and data analytics; and
                </li>
                <li>
                  ensuring the proper and more efficient working of the IT
                  systems and services.
                </li>
              </ol>
            </li>
            <li>
              SEEK Volunteer may also receive Personal Information from SEEK
              Volunteer&apos;s Related Bodies Corporate from time to time in
              accordance with the privacy policy of those Related Bodies
              Corporate and/or the Privacy Act 1988 (Cth) (as applicable).
            </li>
            <li>
              We may also disclose your Personal Information to third parties
              where it is required by Law, such as for law enforcement purposes
              or where there is a threat to a person&apos;s safety.
            </li>
            <li>
              Our Sites may from time to time contain links to other websites.
              These websites may have a different privacy policy to ours, and
              you should check this with them.
            </li>
          </ol>
        </li>

        <li>
          <strong>How do we keep your personal information safe?</strong>
          <ol type="a">
            <li>
              SEEK Volunteer takes all reasonable steps to ensure the security
              of our system and to protect your information from misuse,
              interference and loss as well as unauthorised access, modification
              or disclosure.
            </li>
            <li>
              Any information which we hold for you is stored on secure servers
              in Australia.
            </li>
            <li>
              In addition, our employees and the contractors who provide
              services related to our information systems are obliged to respect
              the confidentiality and privacy of any Personal Information held
              by Seek Volunteer. Except in the case of breaches of our
              obligations under the Privacy Act to protect your Personal
              Information, Seek Volunteer will not be held responsible for
              events arising from unauthorised access of your Personal
              Information.
            </li>
          </ol>
        </li>

        <li>
          <strong>Can I correct or update my personal information?</strong>
          <ol type="a">
            <li>
              If you have registered with us as an Organisation, you may at any
              time access and modify the Personal Information you provided in
              the registration form by visiting your profile page.
            </li>
            <li>
              Users who have asked SEEK Volunteer to pass their Personal
              Information to an Organisation can request their Personal
              Information directly from that Organisation.
            </li>
            <li>
              We retain information you provide to us and which we collect about
              you for so long as we continue to provide services to you.
              Organisations can delete their account at any time by contacting
              us using the contacting details in the &ldquo;Contact Us&rdquo;
              section below or by visiting the &ldquo;Help and Support&rdquo;
              area of the Site.
            </li>
          </ol>
        </li>

        <li>
          <strong>Email alerts and other communications</strong>
          <ol type="a">
            <li>
              Users may subscribe to receive emails regarding new volunteering
              opportunities that match searches you have previously undertaken
              by providing SEEK Volunteer with your email address. Users can
              unsubscribe from such emails at any time by clicking on the
              unsubscribe link in the email.
            </li>
            <li>
              If you apply to an opportunity on the Sites or you save a search,
              SEEK Volunteer may also provide you with selected information
              about volunteer opportunities and related advice in email
              communications from time to time. SEEK Volunteer may also contact
              you by telephone. You agree that SEEK Volunteer may contact you
              about products and services used by you, and to offer additional
              products and services. You may &lsquo;opt-out&rsquo; of receiving
              emails from SEEK Volunteer by clicking &lsquo;unsubscribe&rsquo;
              in the footer of an email received. When we speak to you over the
              phone we may record the calls for training, verification and
              quality assurance purposes. Any Personal Information collected on
              those calls is used in accordance with this Privacy Statement.
            </li>
            <li>
              From time to time we may contact Organisations by email or phone
              in relation to their use of the Site, new features and other
              volunteer-related content.
            </li>
          </ol>
        </li>

        <li>
          <strong>Feedback and Complaints</strong>
          <ol type="a">
            <li>
              SEEK Volunteer welcomes ideas and feedback about all aspects of
              the Sites. SEEK Volunteer stores feedback that users send to us.
              This feedback may be used to administer and refine the service we
              provide and may be shared with SEEK Volunteer&apos;s Related
              Bodies Corporate either in aggregate form or with specific
              identifying characteristics removed.
            </li>
            <li>
              If you have any complaints about our dealings with your Personal
              Information or any questions regarding this Privacy Statement you
              are able to submit that complaint or query by contacting us using
              the methods detailed in the &ldquo;Contact Us&rdquo; paragraph
              below. Any complaints received by us will be referred to our
              customer service team for prompt investigation and a response will
              be provided to you as soon as possible. Should you not be
              satisfied with the resolution of any complaints made you are able
              to seek further redress through the Office of the Australian
              Information Commissioner (see{" "}
              <ExternalLink href="https://www.oaic.gov.au">
                https://www.oaic.gov.au
              </ExternalLink>{" "}
              for further information).
            </li>
          </ol>
        </li>

        <li>
          <strong>Contacting us</strong>
          <br />
          If at any time you have any questions regarding this Privacy
          Statement, the practices of this Site or dealings with this Site, you
          can{" "}
          <Link
            to={{
              pathname: AppRoutes.ContactUs,
              state: {
                subject: "Privacy Statement Query",
              },
            }}
          >
            contact us
          </Link>{" "}
          .
        </li>

        <li>
          <strong>How will this privacy statement be updated?</strong>
          <ol type="a">
            <li>
              We may modify this Privacy Policy at any time. You should review
              this Privacy Policy periodically so that you are updated on any
              changes. In some circumstances, depending on the nature of the
              changes, you will be notified of the changes to this Privacy
              Policy prior to them taking effect if required to do so under
              applicable Privacy Laws.
            </li>
            <li>
              By continuing to use our Site, products or services, you agree to
              any modifications that are made to this Privacy Policy.
            </li>
          </ol>
        </li>

        <Definitions
          showAgency
          showLaw
          showPartners
          showPersonalInformation
          showPrivacyLaws
          showRelatedBodyCorporate
          showServiceProviders
        />
      </ol>
    </>
  );
};

const TermsForVolunteers = () => {
  return (
    <>
      <h2>Terms and Conditions of Use for Volunteers</h2>

      <p>
        This page states the terms of use (&ldquo;<b>Terms</b>&rdquo;) under
        which you as a volunteer (&ldquo;<b>You</b>&rdquo;) may use our SEEK
        Volunteer and partner websites{" "}
        <a href={`https://${AU_APPLICATION_URL}`}>{AU_APPLICATION_URL}</a>,{" "}
        <a href={`https://${NZ_APPLICATION_URL}`}>{NZ_APPLICATION_URL}</a>, and{" "}
        <a href={`https://${NSW_APPLICATION_URL}`}>{NSW_APPLICATION_URL}</a>{" "}
        (&ldquo;the&nbsp;<b>Site</b>&rdquo;). Please read them carefully as they
        affect Your rights and liabilities under the law. If You do not agree to
        these Terms, please do not register for an account or use our Site.
        These Terms are effective on the date they are published on our Site.
      </p>
      <p>
        By using the Site You understand that listed volunteer organisations are
        independent organisations responsible for their own actions and not
        agents of SEEK Volunteer. Whilst listed volunteer organisations have
        represented to SEEK Volunteer that they have volunteer insurance, SEEK
        Volunteer cannot warrant this, and recommend that you substantiate this
        with individual organisations before accepting a volunteer position.
      </p>
      <ol
        css={`
          > li {
            margin-bottom: 0.5rem;
          }
        `}
      >
        <li>
          <strong>The Terms and Conditions</strong>
          <ol type="a">
            <li>
              Our Site is provided to You for your use subject to these Terms.
              These Terms form a binding agreement between You and SEEK
              Volunteer. By accessing or using our Site, You agree to accept and
              be bound by these Terms.
            </li>
            <li>
              We may update these Terms from time to time and the updated
              version will apply to You. Our current Terms will be accessible
              through our Site.
            </li>
            <li>
              If we reasonably believe that changes to these Terms will be
              materially detrimental to You, we will provide a notice on our
              Site 30 days prior to the changes taking effect.
            </li>
            <li>
              If You do not wish to accept the new Terms, You should not
              continue to use our Site. If You continue to use our Site, your
              use will indicate your agreement to be bound by the new Terms.
            </li>
            <li>
              Your use of the Site is also subject to the SEEK Volunteer{" "}
              <Link to="/legal/privacy-statement">privacy policy</Link>. Please
              read this policy to understand how we use and protect your
              personal information.
            </li>
          </ol>
        </li>

        <li>
          <strong>Operation of Site</strong>
          <ol type="a">
            <li>
              The Site enables volunteers such as yourself to access volunteer
              opportunities. These volunteer opportunities will be posted on the
              Site by not-for-profit organisations, social enterprises and
              agencies (<b>Organisations</b>). Where You provide us with
              information in relation to a particular volunteer opportunity, we
              will endeavour to provide that information to the relevant
              Organisation. The Site may also include other information of a
              general nature, links to other websites and information.
            </li>
            <li>
              While we technically operate the Site, we exercise no editorial
              control over much of the Content on the Site, and in most cases
              the Content on the Site is provided by other persons, such as
              Organisations and other third parties (<b>Third Party Content</b>
              ).
            </li>
            <li>
              The Site only seeks to match your interests in pursuing
              volunteering with similar opportunities to participate in
              volunteering activities offered by Organisations. Where such
              similarities exist, we will provide the information You Post on
              the Site to one or more Organisations. Those Organisations will
              then contact You directly and it is the responsibility of You and
              the Organisation to determine whether You both wish to pursue the
              volunteering opportunity.
            </li>
            <li>
              Your correspondence or dealings with any Organisation or any other
              third parties (including, without limitation, Agencies) are solely
              between you and those Organisations or third parties. We are
              making no recommendation in relation to the appropriateness of any
              volunteering opportunity. We are not responsible in any way for
              any legal or other requirements relating to any volunteering
              opportunity. You agree that we will not be liable or responsible
              for any loss or damage of any sort incurred as the result of your
              dealings with such Organisation or third party.
            </li>
            <li>
              We do not monitor or exercise editorial control over Third Party
              Content, however we reserve the right at our discretion to:
              <ol type="i">
                <li>access or examine any Third-Party Content;</li>
                <li>
                  refuse to provide any Content provided by You to
                  not-for-profit organisations (regardless of whether you were
                  responding to a particular volunteer opportunity); and
                </li>
                <li>
                  move, remove or disable access to Third Party Content which we
                  consider, in our sole opinion, to breach any Law or to be
                  otherwise unacceptable.
                </li>
              </ol>
            </li>
          </ol>
        </li>

        <li>
          <strong>Your Use of the Site</strong>
          <ol type="a">
            <li>
              You must not Post any Content that:
              <ol type="i">
                <li>You do not have the right to Post;</li>
                <li>
                  is defamatory or in contempt of any legal or other
                  proceedings;
                </li>
                <li>is misleading or deceptive;</li>
                <li>
                  incites hatred or discrimination against any group of persons
                  being a group defined by reference to colour, race, sex,
                  origin, nationality or ethnic or national origins;
                </li>
                <li>denounces religious or political beliefs;</li>
                <li>
                  includes religious or political material which is or is likely
                  to be offensive;
                </li>
                <li>
                  is indecent, obscene, vulgar, pornographic, offensive or of
                  doubtful propriety or of a menacing character or is likely to
                  annoy or concern;
                </li>
                <li>
                  infringes any copyright, trademark, patent or other
                  intellectual property right of another person;
                </li>
                <li>
                  contains any unsolicited or unauthorised advertising or
                  promotional material;
                </li>
                <li>
                  contains viruses or similar software or data which is designed
                  to interrupt, destroy or limit the functionality of any
                  computer software or hardware; or
                </li>
                <li>
                  impersonates any person or misrepresents your relationship
                  with any person.
                </li>
              </ol>
            </li>
            <li>
              You must not:
              <ol type="i">
                <li>
                  disrupt or interfere with the Site or servers or other
                  software, hardware or equipment connected to or via the Site;
                </li>
                <li>violate any Law relating to your use of the Site; or</li>
                <li>
                  collect or store personal data about other users of the Site.
                </li>
              </ol>
            </li>
          </ol>
        </li>

        <li>
          <strong>Intellectual Property Rights</strong>
          <ol type="a">
            <li>
              You acknowledge that Content on the Site is subject to copyright
              and possibly other intellectual property rights (
              <b>Intellectual Property Rights</b>).
            </li>
            <li>
              Unless you are expressly authorised by law you must not yourself,
              or participate in or permit any other person, to:
              <ol type="i">
                <li>
                  sell, reproduce, distribute, modify, display, publicly
                  perform, prepare derivative works based on, repost or
                  otherwise use any of the Content in any way for any public or
                  commercial purpose without our prior written consent;
                </li>
                <li>
                  use any Content on any other web site or in a networked
                  computer environment for any purpose; or
                </li>
                <li>
                  otherwise infringe the Intellectual Property Rights of any
                  person in using the Site or any Content.
                </li>
              </ol>
            </li>
            <li>
              Nothing you do on or in relation to the Site will transfer any
              Intellectual Property Rights to you or licence you to exercise any
              Intellectual Property Rights unless this is expressly stated.
            </li>
            <li>
              In relation to any Content Posted by you, but subject to our
              privacy policy discussed below, you grant us a perpetual,
              irrevocable, royalty-free licence to use that content for the
              purposes for which the Site exists.
            </li>
          </ol>
        </li>

        <li>
          <strong>Links</strong>
          <ol type="a">
            <li>
              We have not reviewed all the sites linked to this Site and are not
              responsible for the content or accuracy of any off-site pages or
              any other sites linked to this Site (including without limitation
              sites linked through advertisements). The inclusion of any link
              does not imply that we endorse the linked site, and you use the
              links at your own risk.
            </li>
          </ol>
        </li>

        <li>
          <strong>Privacy</strong>
          <ol type="a">
            <li>
              In using the Site you may give us personal information. Please
              refer to our{" "}
              <Link to="/legal/privacy-statement">privacy policy</Link> for
              further information.
            </li>
            <li>
              You acknowledge and agree that Organisations may engage Agencies
              to assist them with regard to volunteer recruitment. In this
              instance, You agree to the Organisation providing the Agency with
              the ability to:
              <ol type="i">
                <li>
                  view summary information of your volunteer application/s,
                </li>
                <li>
                  view volunteer application/s in full to provide shortlisting
                  services; and
                </li>
                <li>
                  contact you on behalf of the Organisation in order to provide
                  recruitment support to the Organisation.
                </li>
              </ol>
            </li>
          </ol>
        </li>

        <li>
          <strong>Use of the Site is at Your Risk</strong>
          <ol type="a">
            <li>
              You use the Site at your risk. You must evaluate, and bear all
              risks associated with, the use of any Content, including reliance
              on the accuracy, completeness or usefulness of any Content. You
              should seek your own independent advice with respect to any
              Content.
            </li>
            <li>
              We endeavour to provide a convenient and functional Site, but we
              do not guarantee that the Content will be error free or that the
              Site or the server that operates it are free of viruses or other
              harmful components.
            </li>
            <li>
              If your use of the Site results in the need for servicing or
              replacing property, material, equipment or data, we will not be
              responsible for such costs.
            </li>
            <li>
              Without limiting the above provisions, you use the Site at your
              risk and everything on the Site is provided to you &ldquo;as
              is&rdquo; and &ldquo;as available&rdquo; without warranty or
              condition of any kind, either expressed or implied, including, but
              not limited to, any implied warranties of merchantability, fitness
              for a particular purpose, or non-infringement. To the extent
              permitted by law, we expressly disclaim and exclude any
              representation or warranty whether express or implied under or in
              relation to your use of the Site. Where such a condition cannot be
              excluded then, to the extent permitted by law, our liability is
              limited, at our option, to re-supply of the good or service to
              which the condition relates or the payment of the cost of such a
              re-supply.
            </li>
          </ol>
        </li>

        <li>
          <strong>Limitation of Liability</strong>
          <ol type="a">
            <li>
              To the extent permitted by law we have no liability to You,
              whether for breach of these terms or in negligence or in any other
              tort or for any other common law or statutory cause of action
              arising in relation to these terms, the Site or the Content.
            </li>
            <li>
              To the extent permitted by law no party involved in creating,
              producing, or delivering the Site or any Content will be liable
              for any direct, incidental, consequential or indirect damages,
              loss or corruption of data, loss of profits, goodwill, bargain or
              opportunity or loss of anticipated savings resulting from your
              access to, or use of, or inability to use the Site and the
              Content, whether based on warranty, contract, tort, negligence or
              any other legal theory, and whether or not we know of the
              possibility of such damage.
            </li>
          </ol>
        </li>

        <li>
          <strong>General Provisions</strong>
          <ol type="a">
            <li>
              We will give you any necessary notices by posting them on the
              Site. You agree:
              <ol type="i">
                <li>to check the Site for notices; and</li>
                <li>
                  that You will be considered to have received a notice when it
                  is made available to You by posting on the Site.
                </li>
              </ol>
            </li>
            <li>
              You acknowledge that we may, in our sole discretion and with or
              without notice,
              <ol type="i">
                <li>vary the Site or any part of the Site; and</li>
                <li>
                  modify or discontinue this Site, any part of the Site and the
                  services available on it without notice.
                </li>
              </ol>
            </li>
            <li>
              The provisions of these terms and conditions entitled &ldquo;Use
              of the Site is at Your Risk&rdquo;, &ldquo;Limitation of
              Liability,&rdquo; and &ldquo;General Provisions&rdquo; will
              survive cancellation of your registration or termination of your
              use of the Site.
            </li>
            <li>
              If any part of these Terms is held to be unenforceable, the
              unenforceable part will be severed and will not affect the
              validity or enforceability of the remaining provisions of these
              Terms.
            </li>
            <li>
              These Terms will be governed by the laws of Victoria, Australia.
              You irrevocably submit to the exclusive jurisdiction of the courts
              of Victoria, Australia.
            </li>
            <li>
              You agree to defend, indemnify, and hold harmless us, our
              officers, directors, employees and agents, from and against any
              claims, actions or demands, including without limitation
              reasonable legal and accounting fees, alleging or resulting from
              your use of the Site or the Content or your breach of these Terms.
            </li>
          </ol>
        </li>
        <Definitions showAgency showLaw showContent showPost showWeUs />
      </ol>
    </>
  );
};

const TermsForOrganisations = () => {
  return (
    <>
      <h2>Terms and Conditions of Use for Organisations</h2>

      <p>
        This page states the terms of use (&ldquo;<b>Terms</b>&rdquo;) under
        which you as a not-for-profit organisation, social enterprise or Agency
        (&ldquo;<b>You</b>&rdquo; or &ldquo;<b>Organisation</b>&rdquo;) may use
        our SEEK Volunteer and partner websites{" "}
        <a href={`https://${AU_APPLICATION_URL}`}>{AU_APPLICATION_URL}</a>,{" "}
        <a href={`https://${NZ_APPLICATION_URL}`}>{NZ_APPLICATION_URL}</a>, and{" "}
        <a href={`https://${NSW_APPLICATION_URL}`}>{NSW_APPLICATION_URL}</a>{" "}
        (&ldquo;the&nbsp;<b>Site</b>&rdquo;). Please read them carefully as they
        affect Your rights and liabilities under the law. If You do not agree to
        these Terms, please do not register for an account or use our Site.
        These Terms are effective on the date they are published on our Site.
      </p>
      <ol
        css={`
          > li {
            margin-bottom: 0.5rem;
          }
        `}
      >
        <li>
          <strong>These Terms and Conditions</strong>
          <ol type="a">
            <li>
              Our Site is provided to You for your use subject to these Terms.
              These Terms form a binding agreement between You and SEEK
              Volunteer. By accessing or using our Site, You agree to accept and
              be bound by these Terms.
            </li>
            <li>
              We may update these Terms from time to time and the updated
              version will apply to You. Our current Terms will be accessible
              through our Site.
            </li>
            <li>
              If we reasonably believe that changes to these Terms will be
              materially detrimental to You, we will provide a notice on our
              Site 30 days prior to the changes taking effect.
            </li>
            <li>
              If You do not wish to accept the new Terms, You should not
              continue to use our Site. If You continue to use our Site, your
              use will indicate your agreement to be bound by the new Terms.
            </li>
            <li>
              Your use of the Site is also subject to the SEEK Volunteer{" "}
              <Link to="/legal/privacy-statement">privacy policy</Link>.
            </li>
          </ol>
        </li>

        <li>
          <strong>Operation of Site</strong>
          <ol type="a">
            <li>
              Where a potential volunteer provides us with information in
              relation to a volunteer opportunity Posted by you, we will
              endeavour to provide that information to You.
            </li>
            <li>
              The Site may include information posted by Organisations
              (including information posted by You), potential volunteers and
              other users of the Site. The Site may also include other
              information of a general nature, links to other websites and
              information.
            </li>
            <li>
              c. While we technically operate the Site we exercise no editorial
              control over much of the Content on the Site, and in most cases
              the Content on the Site is provided by other persons, such as
              potential volunteers and other users of the Site, Organisations
              (including yourself) and other third parties (
              <b>Third Party Content</b>).
            </li>
            <li>
              Your correspondence or dealings with any potential volunteers or
              any other third parties are solely between You and those potential
              volunteers or third parties. You are responsible for selecting
              volunteers that are suitable for your Organisation and for meeting
              all legal and other requirements in relation to those volunteers.
              We make no recommendation regarding the suitability of any
              volunteer. You agree that we will not be liable or responsible for
              any loss or damage of any sort incurred as the result of your
              dealings with volunteers.
            </li>
            <li>
              We do not monitor or exercise editorial control over Third Party
              Content, however we reserve the right at our discretion to:
              <ol type="i">
                <li>access or examine any Third Party Content;</li>
                <li>
                  refuse to provide any Content Posted by a potential volunteer
                  to You (regardless of whether that potential volunteer was
                  responding to a volunteer opportunity Posted by You); and
                </li>
                <li>
                  move, remove or disable access to Third Party Content which we
                  consider, in our sole opinion, to breach any Law or to be
                  otherwise unacceptable.
                </li>
              </ol>
            </li>
          </ol>
        </li>

        <li>
          <strong>Registration</strong>
          <ol type="a">
            <li>
              The Site enables Organisations to Post volunteer opportunities to
              the Site for access by potential volunteers. You will be required
              to register with us in order to Post volunteer opportunities to
              the Site. When you register:
              <ol type="i">
                <li>
                  You must provide accurate, complete and updated registration
                  information;
                </li>
                <li>
                  You must safeguard any user name and password which is
                  provided to You;
                </li>
                <li>
                  You authorise us to assume that any person using the Site with
                  your user name and password is either You or is authorised to
                  act for You; and
                </li>
                <li>
                  You may cancel such registration at any time by notifying us.
                </li>
              </ol>
            </li>
            <li>
              You acknowledge and agree that we are under no obligation to
              accept any application for registration by You or any other
              person, including without limitation if You fail to meet our
              registration criteria or for any other reason.
            </li>
            <li>
              We may cancel your registration if You do not visit the Site or
              update any of your Postings on the Site for an extended period of
              time, if You fail to meet our registration criteria as amended
              from time to time, or if we reasonably believe that You have
              violated any Laws, acted inconsistently with the spirit of these
              Terms, or have violated our rights or those of another party.
            </li>
          </ol>
        </li>

        <li>
          <strong>Your Use of the Site</strong>
          <ol type="a">
            <li>
              You must not Post any Content that:
              <ol type="i">
                <li>You do not have the right to Post;</li>
                <li>
                  is defamatory or in contempt of any legal or other
                  proceedings;
                </li>
                <li>is misleading or deceptive;</li>
                <li>
                  incites hatred or discrimination against any group of persons
                  being a group defined by reference to colour, race, sex,
                  origin, nationality or ethnic or national origins;
                </li>
                <li>denounces religious or political beliefs;</li>
                <li>
                  includes religious or political material which is or is likely
                  to be offensive;
                </li>
                <li>
                  is indecent, obscene, vulgar, pornographic, offensive or of
                  doubtful propriety or of a menacing character or is likely to
                  annoy or concern any person;
                </li>
                <li>
                  infringes any copyright, trade mark, patent or other
                  intellectual property right of another person;
                </li>
                <li>
                  contains any unsolicited or unauthorised advertising or
                  promotional material;
                </li>
                <li>
                  contains viruses or similar software or data which is designed
                  to interrupt, destroy or limit the functionality of any
                  computer software or hardware; or
                </li>
                <li>
                  impersonates any person or misrepresents your relationship
                  with any person.
                </li>
              </ol>
            </li>
            <li>
              You must not:
              <ol type="i">
                <li>
                  disrupt or interfere with the Site or servers or other
                  software, hardware or equipment connected to or via the Site;
                </li>
                <li>violate any Law relating to your use of the Site; or</li>
                <li>
                  collect or store personal data about other users of the Site.
                </li>
              </ol>
            </li>
            <li>
              You must not:
              <ol type="i">
                <li>
                  include links to external websites in the opportunities You
                  post and/or ask volunteers to apply or express interest via a
                  different website;
                </li>
                <li>
                  include other means of application (eg phone, email, etc) in
                  your opportunity listing. All applications must go via the
                  'express interest' button.
                </li>
              </ol>
            </li>
            <li>
              You must adhere to{" "}
              <ExternalLink href="https://www.volunteeringaustralia.org/resources/definition-of-volunteering">
                Volunteering Australia's Definition of Volunteering
              </ExternalLink>{" "}
              when listing opportunities.
            </li>
            <li>
              You warrant that You will not ask a potential volunteer to provide
              you any consideration to secure a volunteer position for them.
            </li>
            <li>
              You confirm that each time you post an opportunity on SEEK
              Volunteer&apos;s Site, You will have in place adequate policies of
              insurance taking into account the nature of your organisation and
              the role being advertised.
            </li>
          </ol>
        </li>

        <li>
          <strong>Assistance from Agency</strong>
          <ol type="a">
            <li>
              You may choose to connect Your account with an Agency for the
              purpose of receiving support. In this instance, You (as the
              Organisation) agree to provide the Agency with the ability to:
              <ol type="i">
                <li>
                  view your relevant volunteer opportunities/advertisements to
                  provide recruitment support
                </li>
                <li>
                  view a summary information of your volunteer applications, or
                </li>
                <li>
                  view your volunteer applications in full to provide
                  shortlisting services
                </li>
              </ol>
            </li>
          </ol>
        </li>

        <li>
          <strong>SEEK Volunteer Widget</strong>
          <ol type="a">
            <li>
              You may choose to have a widget on your website (at no charge)
              which will automatically replicate the opportunities on the Site,
              (&ldquo;SEEK Volunteer Widget&rdquo;).
            </li>
            <li>
              If you choose to have a SEEK Volunteer Widget on your website:
              <ol type="i">
                <li>
                  we grant You a limited, revocable, non-transferable,
                  royalty-free licence to use our Intellectual Property in the
                  SEEK Volunteer Widget (including the &lsquo;powered by SEEK
                  Volunteer&rsquo; logo) until You stop using the SEEK Volunteer
                  Widget or we ask that You stop using the SEEK Volunteer
                  Widget;
                </li>
                <li>
                  You must not modify the &lsquo;powered by SEEK
                  Volunteer&rsquo; logo in any way including, without
                  limitation, resizing, recolouring, stretching or change the
                  background appearance;
                </li>
                <li>
                  You must not provide the code underlying the SEEK Volunteer
                  Widget (or any related Intellectual Property) to any other
                  party without our express consent.
                </li>
                <li>
                  You must not create derivate works based on our Intellectual
                  Property.
                </li>
                <li>
                  You acknowledge and agree that:
                  <ol type="1">
                    <li>
                      we own all Intellectual Property rights in the SEEK
                      Volunteer Widget, and the licence granted above does not
                      purport to change this position.
                    </li>
                    <li>
                      we are permitted, on reasonable notice and during business
                      hours, to undertake a review of your compliance with our
                      Terms around your use of the SEEK Volunteer Widget.
                    </li>
                    <li>
                      if requested by us, You will provide us with access to
                      your place of business, systems, data, reports, documents
                      or other materials (as applicable) for us to conduct such
                      audit.
                    </li>
                  </ol>
                </li>
              </ol>
            </li>
            <li>
              As the SEEK Volunteer Widget is provided for no consideration, we
              disclaim all liability associated with the provision of the SEEK
              Volunteer Widget (to the maximum extent permitted by law).
            </li>
            <li>
              When You stop using the SEEK Volunteer Widget, You must also cease
              using all works containing our Intellectual Property on your
              website.
            </li>
          </ol>
        </li>

        <li>
          <strong>Intellectual Property Rights</strong>
          <ol type="a">
            <li>
              We do not own any pre-existing intellectual property owned by You
              (an example of which may be your company logo). However, You agree
              to provide us with a perpetual, non-transferable, royalty free
              licence to use your intellectual property in the products and
              services we provide (or make available) to You. If You withdraw
              this licence, we may be unable to provide certain products and
              services to You.
            </li>
            <li>
              SEEK Volunteer (or our parent company SEEK Limited) own all
              Intellectual Property rights associated with our Site. Subject to
              the above paragraph 7a., SEEK Volunteer (or our parent company
              SEEK Limited) retains all Intellectual Property rights in our
              Site.
            </li>
            <li>
              Unless You are expressly authorised by Law you must not yourself,
              or participate in or permit any other person, to:
              <ol type="i">
                <li>
                  sell, reproduce, distribute, modify, display, publicly
                  perform, prepare derivative works based on, repost or
                  otherwise use any of the Content in any way for any public or
                  commercial purpose without our prior written consent;
                </li>
                <li>
                  use any Content on any other web site or in a networked
                  computer environment for any purpose; or
                </li>
                <li>
                  otherwise infringe the Intellectual Property rights of any
                  person in using the Site or any Content.
                </li>
              </ol>
            </li>
            <li>
              Nothing You do on or in relation to the Site will transfer any
              Intellectual Property rights to You or licence You to exercise any
              Intellectual Property rights unless this is expressly stated.
            </li>
            <li>
              In relation to any Content Posted by You, you grant us a
              perpetual, irrevocable, royalty-free licence throughout the world
              to use, reproduce, modify, adapt, publish, translate, sublicense,
              create derivate works from, incorporate in other works (whether
              electronic or not), distribute, perform and display such Content,
              whether in whole or in part.
            </li>
          </ol>
        </li>

        <li>
          <strong>Links</strong>
          <ol type="a">
            <li>
              We have not reviewed all of the sites linked to this Site and are
              not responsible for the content or accuracy of any off-site pages
              or any other sites linked to this Site (including without
              limitation sites linked through advertisements). The inclusion of
              any link does not imply that we endorse the linked site, and You
              use the links at your own risk.
            </li>
          </ol>
        </li>

        <li>
          <strong>Privacy</strong>
          <ol type="a">
            <li>
              In using the Site you may give us personal information. Please
              refer to our{" "}
              <Link to="/legal/privacy-statement">privacy policy</Link> for
              further information.
            </li>
          </ol>
        </li>

        <li>
          <strong>Use of the Site is at Your Risk</strong>
          <ol type="a">
            <li>
              You use the Site at your risk. You must evaluate, and bear all
              risks associated with, the use of any Content, including reliance
              on the accuracy, completeness or usefulness of any Content. You
              should seek your own independent advice with respect to any
              Content.
            </li>
            <li>
              We endeavour to provide a convenient and functional Site, but we
              do not guarantee that the Content will be error free or that the
              Site or the server that operates it are free of viruses or other
              harmful components.
            </li>
            <li>
              If your use of the Site results in the need for servicing or
              replacing property, material, equipment or data, we will not be
              responsible for such costs.
            </li>
            <li>
              Without limiting the above provisions, You use the Site at your
              risk and everything on the Site is provided to You "as is" and "as
              available" without warranty or condition of any kind, either
              expressed or implied, including, but not limited to, any implied
              warranties of merchantability, fitness for a particular purpose,
              or non-infringement. To the extent permitted by law, we expressly
              disclaim and exclude any representation or warranty whether
              express or implied under or in relation to your use of the Site.
              Where such a condition cannot be excluded then, to the extent
              permitted by law, our liability is limited, at our option, to
              re-supply of the good or service to which the condition relates or
              the payment of the cost of such a re-supply.
            </li>
          </ol>
        </li>

        <li>
          <strong>Limitation of Liability</strong>
          <ol type="a">
            <li>
              To the extent permitted by law we have no liability to You,
              whether for breach of these Terms or in negligence or in any other
              tort or for any other common law or statutory cause of action
              arising in relation to these Terms, the Site or the Content.
            </li>
            <li>
              To the extent permitted by law no party involved in creating,
              producing, or delivering the Site or any Content will be liable
              for any direct, incidental, consequential or indirect damages,
              loss or corruption of data, loss of profits, goodwill, bargain or
              opportunity or loss of anticipated savings resulting from your
              access to, or use of, or inability to use the Site and the
              Content, whether based on warranty, contract, tort, negligence or
              any other legal theory, and whether or not we know of the
              possibility of such damage.
            </li>
          </ol>
        </li>

        <li>
          <strong>General Provisions</strong>
          <ol type="a">
            <li>
              We will give you any necessary notices by posting them on the
              Site. You agree:
              <ol type="i">
                <li>to check the Site for notices; and</li>
                <li>
                  that You will be considered to have received a notice when it
                  is made available to You by posting on the Site.
                </li>
              </ol>
            </li>
            <li>
              You acknowledge that we may, in our sole discretion and with or
              without notice,
              <ol type="i">
                <li>vary the Site or any part of the Site; and</li>
                <li>
                  modify or discontinue this Site, any part of the Site and the
                  services available on it without notice.
                </li>
              </ol>
            </li>
            <li>
              The provisions of these Terms entitled &ldquo;Use of the Site is
              at Your Risk&rdquo;, &ldquo;Limitation of Liability,&rdquo; and
              &ldquo;General Provisions&rdquo; will survive cancellation of your
              registration or termination of your use of the Site.
            </li>
            <li>
              If any part of these Terms is held to be invalid or unenforceable,
              the unenforceable part will be severed and will not affect the
              validity or enforceability of the remaining provisions of these
              Terms.
            </li>
            <li>
              These Terms will be governed by the laws of Victoria, Australia.
              You irrevocably submit to the exclusive jurisdiction of the courts
              of Victoria, Australia.
            </li>
            <li>
              You agree to defend, indemnify, and hold harmless us, our
              officers, directors, employees and agents, from and against any
              claims, actions or demands, including without limitation
              reasonable legal and accounting fees, alleging or resulting from
              your use of the Site or the Content or your breach of these Terms.
            </li>
          </ol>
        </li>

        <Definitions
          showAgency
          showLaw
          showContent
          showIntellectualProperty
          showPost
          showWeUs
        />
      </ol>
    </>
  );
};

export const Legal = {
  PrivacyContent,
  TermsForVolunteers,
  TermsForOrganisations,
};
